import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { extendedManagementApi } from '../api/managementService';

const rateLimitSchema = z.object({
  auth: z.number().int().positive().optional(),
  heavy: z.number().int().positive().optional(),
  backfill: z.number().int().positive().optional(),
  admin: z.number().int().positive().optional(),
  standard: z.number().int().positive().optional(),
  light: z.number().int().positive().optional(),
  write: z.number().int().positive().optional(),
});

type RateLimitFormData = z.infer<typeof rateLimitSchema>;

interface RateLimitConfig {
  defaults: Record<string, number>;
  overrides: Partial<Record<string, number>>;
  effective: Record<string, number>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
}

const RATE_LIMIT_TIERS = [
  {
    key: 'auth',
    name: 'Authentication',
    description: 'Login and auth endpoints (per 15 minutes)',
    defaultValue: 5,
  },
  {
    key: 'heavy',
    name: 'Heavy Queries',
    description: 'Aggregations and telemetry (per minute)',
    defaultValue: 20,
  },
  {
    key: 'backfill',
    name: 'Backfill Operations',
    description: 'Data gap filling (per hour)',
    defaultValue: 5,
  },
  {
    key: 'admin',
    name: 'Admin Operations',
    description: 'Admin endpoints (per minute)',
    defaultValue: 30,
  },
  {
    key: 'standard',
    name: 'Standard Queries',
    description: 'Regular list queries (per minute)',
    defaultValue: 60,
  },
  {
    key: 'light',
    name: 'Light Operations',
    description: 'Single resource lookups (per minute)',
    defaultValue: 120,
  },
  {
    key: 'write',
    name: 'Write Operations',
    description: 'Create/update/delete (per minute)',
    defaultValue: 30,
  },
];

export default function ApiClientRateLimits({ isOpen, onClose, clientId, clientName }: Props) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: config, isLoading } = useQuery<RateLimitConfig>({
    queryKey: ['apiClient', clientId, 'rateLimits'],
    queryFn: async () => {
      return await extendedManagementApi.getApiClientRateLimits(clientId);
    },
    enabled: isOpen && !!clientId,
  });

  const form = useForm<RateLimitFormData>({
    resolver: zodResolver(rateLimitSchema),
  });

  // Load current overrides into form
  useEffect(() => {
    if (config?.overrides) {
      form.reset(config.overrides);
    }
  }, [config, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: RateLimitFormData) => {
      // Only send fields that have values
      const overrides = Object.entries(data)
        .filter(([_, value]) => value !== undefined && value !== null)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      return await extendedManagementApi.updateApiClientRateLimits(clientId, overrides);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiClient', clientId, 'rateLimits'] });
      queryClient.invalidateQueries({ queryKey: ['apiClients'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update rate limits');
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      return await extendedManagementApi.resetApiClientRateLimits(clientId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiClient', clientId, 'rateLimits'] });
      queryClient.invalidateQueries({ queryKey: ['apiClients'] });
      form.reset({});
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to reset rate limits');
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    setError(null);
    updateMutation.mutate(data);
  });

  const handleReset = () => {
    if (confirm('Reset all rate limits to defaults? This cannot be undone.')) {
      setError(null);
      resetMutation.mutate();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-3xl bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Rate Limit Configuration
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-1">{clientName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {isLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-600 mt-2">Loading configuration...</p>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Leave fields empty to use default limits. Only specify custom values for tiers you want to override.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {RATE_LIMIT_TIERS.map((tier) => {
                      const defaultValue = config?.defaults?.[tier.key] || tier.defaultValue;
                      const currentOverride = config?.overrides?.[tier.key];

                      return (
                        <div key={tier.key} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-900">
                                {tier.name}
                              </label>
                              <p className="text-xs text-gray-600 mt-1">
                                {tier.description}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Default: <span className="font-medium">{defaultValue}</span>
                                {currentOverride && (
                                  <span className="ml-2 text-blue-600">
                                    • Current: <span className="font-medium">{currentOverride}</span>
                                  </span>
                                )}
                              </p>
                            </div>
                            <div className="ml-4 w-32">
                              <input
                                type="number"
                                min="1"
                                {...form.register(tier.key as keyof RateLimitFormData, {
                                  valueAsNumber: true,
                                  setValueAs: (v) => v === '' ? undefined : parseInt(v, 10),
                                })}
                                placeholder={defaultValue.toString()}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                              {form.formState.errors[tier.key as keyof RateLimitFormData] && (
                                <p className="mt-1 text-xs text-red-600">
                                  {form.formState.errors[tier.key as keyof RateLimitFormData]?.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-between p-6 border-t bg-gray-50">
              <button
                type="button"
                onClick={handleReset}
                disabled={resetMutation.isPending || !config?.overrides || Object.keys(config.overrides).length === 0}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Reset to Defaults
              </button>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending || isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
